import {
  useRef,
  useState,
  type ReactNode,
} from "react";

const publicAsyncFeedback = {
  catalog: {
    description: "Você pode tentar novamente sem perder seus filtros.",
    message: "Não foi possível carregar os materiais.",
  },
  library: {
    description:
      "Seus dados continuam protegidos. Confira a conexão e tente novamente.",
    message: "Não foi possível carregar seus produtos.",
  },
  orderDetail: {
    description:
      "Nenhum dado financeiro privado foi exibido. Confira a conexão e tente novamente.",
    message: "Não foi possível carregar este pedido.",
  },
  orders: {
    description:
      "Nenhum detalhe privado foi exibido. Confira a conexão e tente novamente.",
    message: "Não foi possível carregar seus pedidos.",
  },
  overview: {
    description:
      "Seus dados continuam protegidos. Confira a conexão e tente novamente.",
    message: "Não foi possível carregar sua visão geral.",
  },
  productDetail: {
    description:
      "Você pode tentar novamente sem perder sua navegação.",
    message: "Não foi possível carregar este material.",
  },
  profile: {
    message: "Não foi possível carregar seu perfil.",
  },
  purchasedProduct: {
    description:
      "Seus dados continuam protegidos. Confira a conexão e tente novamente.",
    message: "Não foi possível carregar este produto.",
  },
  sessions: {
    message: "Não foi possível carregar suas sessões.",
  },
  session: {
    description:
      "Confira sua conexão e tente novamente. Seu conteúdo permanece protegido.",
    message: "Não foi possível confirmar sua sessão.",
  },
  storefrontHome: {
    description:
      "Sua navegação continua segura e você pode tentar novamente.",
    message: "Não foi possível carregar a loja.",
  },
  subscriptions: {
    description:
      "Nenhum detalhe privado foi exibido. Confira a conexão e tente novamente.",
    message: "Não foi possível carregar suas assinaturas.",
  },
} as const;

const fallbackFeedback = {
  description:
    "Confira sua conexão e tente novamente. Nenhum detalhe técnico foi exibido.",
  message: "Não foi possível carregar este conteúdo.",
} as const;

export type PublicAsyncFeedback = keyof typeof publicAsyncFeedback;

type AsyncBoundaryProps =
  | {
      readonly children: ReactNode;
      readonly error?: never;
      readonly headingLevel?: never;
      readonly loadingLabel?: never;
      readonly onRetry?: never;
      readonly retrying?: never;
      readonly secondaryAction?: never;
      readonly status: "success";
    }
  | {
      readonly children: ReactNode;
      readonly error?: never;
      readonly headingLevel?: never;
      readonly loadingLabel: string;
      readonly onRetry?: never;
      readonly retrying?: never;
      readonly secondaryAction?: never;
      readonly status: "loading";
    }
  | {
      readonly children: ReactNode;
      readonly error: PublicAsyncFeedback;
      readonly headingLevel?: "h1" | "h2";
      readonly loadingLabel?: never;
      readonly onRetry: () => unknown;
      readonly retrying?: boolean;
      readonly secondaryAction?: ReactNode;
      readonly status: "error";
    };

function resolveFeedback(key: PublicAsyncFeedback) {
  return Object.hasOwn(publicAsyncFeedback, key)
    ? publicAsyncFeedback[key]
    : fallbackFeedback;
}

export function SkeletonRegion({
  fullscreen = false,
  label,
}: {
  readonly fullscreen?: boolean;
  readonly label: string;
}) {
  return (
    <div
      aria-busy="true"
      aria-label={label}
      aria-live="polite"
      className={`grid content-center gap-4 text-[var(--color-muted)] ${
        fullscreen
          ? "min-h-dvh place-items-center px-6"
          : "min-h-40 py-8"
      }`}
      role="status"
    >
      <span className="sr-only">{label}</span>
      <div
        aria-hidden="true"
        className={`grid w-full gap-3 ${
          fullscreen ? "max-w-xl" : "max-w-3xl"
        }`}
      >
        <span
          className="h-5 w-2/5 animate-pulse rounded-full bg-[var(--app-border)]"
          data-ui-skeleton="true"
        />
        <span
          className="h-20 animate-pulse rounded-[var(--radius-surface)] bg-[var(--color-surface)]"
          data-ui-skeleton="true"
        />
        <span
          className="h-12 w-3/4 animate-pulse rounded-[var(--radius-control)] bg-[var(--color-surface)]"
          data-ui-skeleton="true"
        />
      </div>
    </div>
  );
}

export function AsyncBoundary(props: AsyncBoundaryProps) {
  const retryLock = useRef(false);
  const [retryRequested, setRetryRequested] = useState(false);

  if (props.status === "loading") {
    return <SkeletonRegion label={props.loadingLabel} />;
  }
  if (props.status === "error") {
    const content = resolveFeedback(props.error);
    const Heading = props.headingLevel ?? "h2";
    const retrying = props.retrying === true || retryRequested;
    const retry = () => {
      if (retryLock.current || props.retrying) {
        return;
      }
      retryLock.current = true;
      setRetryRequested(true);
      let result: unknown;
      try {
        result = props.onRetry();
      } catch {
        retryLock.current = false;
        setRetryRequested(false);
        return;
      }
      void Promise.resolve(result).finally(() => {
        retryLock.current = false;
        setRetryRequested(false);
      });
    };

    return (
      <section
        className="grid justify-items-start gap-4 rounded-[var(--radius-surface)] border border-[color-mix(in_oklch,var(--color-error)_35%,transparent)] bg-[color-mix(in_oklch,var(--color-error)_7%,var(--color-surface))] p-5 text-[var(--app-foreground)]"
        role="alert"
      >
        <Heading className="m-0 text-xl font-bold">
          {content.message}
        </Heading>
        {"description" in content ? (
          <p className="m-0 max-w-[62ch] leading-relaxed">
            {content.description}
          </p>
        ) : null}
        <button
          className="min-h-11 rounded-[var(--radius-control)] border border-current px-4 font-bold"
          disabled={retrying}
          onClick={retry}
          type="button"
        >
          {retrying ? "Tentando novamente…" : "Tentar novamente"}
        </button>
        {props.secondaryAction}
      </section>
    );
  }
  return props.children;
}
